import { updateConnectionSuccess } from '@commenda/api';
import type { RootfiCompanyUseCase } from '@prisma/client';
import { useRef } from 'react';

import type { RootfiEnvironment } from '../types/rootfiLink';
import { useRootfiLink } from './useRootfiLink';

interface UseRootfiIntegrationProps {
  corporationId: number | null;
  onSuccess?: () => void;
  refetchIntegrations: () => void;
  onError?: (message: string) => void;
}

interface OpenRootfiLinkOptions {
  /** Suites the reconnect should widen the connection to; posted on connection_success. */
  useCases?: RootfiCompanyUseCase[];
}

function getRootfiEnvironment(): RootfiEnvironment {
  const explicitEnv = process.env.NEXT_PUBLIC_ROOTFI_ENV;
  if (
    explicitEnv === 'staging' ||
    explicitEnv === 'dev' ||
    explicitEnv === 'global'
  ) {
    return explicitEnv;
  }

  if (process.env.NEXT_PUBLIC_CLIENT_ENV === 'production') {
    return 'global';
  }

  const isLocalhost =
    typeof window !== 'undefined' && window.location.hostname === 'localhost';

  return isLocalhost ? 'dev' : 'staging';
}

/**
 * Opens the RootFi OAuth embed, persists the connection via
 * {@link updateConnectionSuccess}, and refetches integration data.
 */
export function useRootFiIntegration({
  corporationId,
  onSuccess,
  refetchIntegrations,
  onError,
}: UseRootfiIntegrationProps) {
  const pendingUseCasesRef = useRef<RootfiCompanyUseCase[] | undefined>(
    undefined
  );

  const { closeLink, openLink, isReady } = useRootfiLink({
    environment: getRootfiEnvironment(),
    onSuccess: async (rootfiData) => {
      try {
        if (corporationId == null) {
          onError?.('No entity selected.');
          return;
        }
        const useCases = pendingUseCasesRef.current;
        const result = await updateConnectionSuccess({
          corporation_id: corporationId,
          integration: rootfiData.integration_type,
          id: rootfiData.rootfi_company_id,
          ...(useCases !== undefined && useCases.length > 0
            ? { useCases }
            : {}),
        });
        if (!result.success) {
          onError?.(
            typeof result.error === 'string' && result.error !== ''
              ? result.error
              : 'Failed to establish a connection. Please try again later.'
          );
          return;
        }
        refetchIntegrations();
        if (onSuccess) onSuccess();
      } catch {
        onError?.('Failed to establish a connection. Please try again later.');
      }
      setTimeout(() => {
        closeLink();
      }, 500);
    },
    onExit: () => {
      closeLink();
    },
  });

  const openRootfiLink = (
    inviteLinkId?: string,
    options?: OpenRootfiLinkOptions
  ) => {
    pendingUseCasesRef.current = options?.useCases;
    openLink(inviteLinkId);
  };

  return {
    closeRootfiLink: closeLink,
    openRootfiLink,
    isRootfiReady: isReady,
  };
}
